import React, { useState, Children } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User as UserIcon, Eye, EyeOff, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
function mapAuthError(err: any): string {
  const code = err?.code ?? '';
  if (code === 'auth/email-already-in-use') return 'An account with this email already exists. Try signing in instead.';
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') return 'Invalid email or password. Please try again.';
  if (code === 'auth/weak-password') return 'Password must be at least 6 characters.';
  if (code === 'auth/invalid-email') return 'Please enter a valid email address.';
  if (code === 'auth/too-many-requests') return 'Too many attempts. Please wait a moment and try again.';
  return err?.message ?? 'Something went wrong. Please try again.';
}
// ─── Shared Layout ──────────────────────────────────────────────
function AuthShell({
  eyebrow,
  title,
  subtitle,
  children
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-1626">
      {/* HERO */}
      <section className="bg-bodybg pt-40 pb-12 px-6 border-b border-darkgray" data-id="element-1627">
        <div className="max-w-2xl mx-auto text-center" data-id="element-1628">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.12
            }
          }
        }} className="flex flex-col items-center" data-id="element-1629">
            <motion.p variants={fadeUp} className="text-[10px] md:text-xs font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-6" data-id="element-1630">
              {eyebrow}
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-serif text-white leading-tight mb-6" data-id="element-1631">
              {title}
            </motion.h1>
            <motion.div variants={fadeUp} className="w-16 h-0.5 bg-tealslate mb-6" data-id="element-1632" />
            <motion.p variants={fadeUp} className="text-base text-[#D8D8D0] max-w-lg leading-relaxed" data-id="element-1633">
              {subtitle}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FORM */}
      <section className="bg-[#F9F9F9] py-16 md:py-24 px-6" data-id="element-1634">
        <div className="max-w-md mx-auto" data-id="element-1635">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} data-id="element-1636">
            {children}
          </motion.div>
        </div>
      </section>
    </div>;
}
function FieldLabel({
  htmlFor,
  children
}: {
  htmlFor: string;
  children: React.ReactNode;
}) {
  return <label htmlFor={htmlFor} className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-1637">
      {children}
    </label>;
}
function ErrorBanner({
  message
}: {
  message: string;
}) {
  return <div className="bg-burgundy/10 border-l-4 border-burgundy p-4 flex gap-3 items-start" data-id="element-1638">
      <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-1639" />
      <p className="text-sm text-burgundy" data-id="element-1640">{message}</p>
    </div>;
}
function SubmitButton({
  loading,
  loadingLabel,
  children
}: {
  loading: boolean;
  loadingLabel: string;
  children: React.ReactNode;
}) {
  return <button type="submit" disabled={loading} className="w-full px-10 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-3" data-id="element-1641">
      {loading ? <>
          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1642" />
          {loadingLabel}
        </> : <>
          {children} <ArrowRight className="w-4 h-4" data-id="element-1643" />
        </>}
    </button>;
}
// ─── Login ──────────────────────────────────────────────────────
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    signIn
  } = useAuth();
  const redirectTo = (location.state as {
    from?: string;
  } | null)?.from ?? '/account';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email, password);
      navigate(redirectTo, {
        replace: true
      });
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };
  return <div className="w-full min-h-screen bg-[#F9F9F9] flex flex-col items-center justify-center pt-32 pb-12 px-6" data-id="element-1644">
      <div className="w-full max-w-[400px]" data-id="element-1645">
        <div className="text-center mb-8" data-id="element-1646">
          <h1 className="text-4xl font-bold text-[#111111] mb-2 tracking-tight" data-id="element-1647">
            Welcome!
          </h1>
          <p className="text-sm font-medium text-[#333333]" data-id="element-1648">
            Access your account and manage your orders
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1649">
          <div data-id="element-1650">
            <label htmlFor="email" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1651">
              Email
            </label>
            <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#E5E5E5] px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all rounded-md shadow-sm" placeholder="Your email" data-id="element-1652" />
          </div>

          <div data-id="element-1653">
            <label htmlFor="password" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1654">
              Password
            </label>
            <div className="relative" data-id="element-1655">
              <input id="password" type={showPassword ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] pl-4 pr-12 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all rounded-md shadow-sm" placeholder="••••••••" data-id="element-1656" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9A9A90] hover:text-tealslate transition-colors" tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-1657">
                {showPassword ? <EyeOff className="w-5 h-5" data-id="element-1658" /> : <Eye className="w-5 h-5" data-id="element-1659" />}
              </button>
            </div>
          </div>

          {error && <ErrorBanner message={error} data-id="element-1660" />}

          <button type="submit" disabled={loading} className="w-full px-6 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed rounded-md flex items-center justify-center gap-2 mt-2 shadow-sm" data-id="element-1661">
            {loading ? <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1662" />
                Signing in…
              </> : 'Sign In'}
          </button>

          <div className="pt-4 flex flex-col items-center gap-3 text-sm" data-id="element-1663">
            <Link to="/forgot-password" className="text-[#5A5A5A] hover:text-[#111111] transition-colors" data-id="element-1664">
              Forgot password?
            </Link>

            <p className="text-[#5A5A5A]" data-id="element-1665">
              Don't have an account?{' '}
              <Link to="/signup" className="text-[#111111] font-bold hover:text-tealslate transition-colors underline underline-offset-2" data-id="element-1666">
                Create one
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>;
}
// ─── Sign Up ────────────────────────────────────────────────────
export function Signup() {
  const navigate = useNavigate();
  const {
    signUp
  } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    setLoading(true);
    try {
      await signUp(email, password, name.trim(), marketingOptIn);
      navigate('/account', {
        replace: true
      });
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };
  return <AuthShell eyebrow="Create Account" title="Start your journey." subtitle="Create an account to track your orders, access digital downloads, and stay connected." data-id="element-1667">
      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1668">
        <div data-id="element-1669">
          <FieldLabel htmlFor="name" data-id="element-1670">Full Name</FieldLabel>
          <div className="relative" data-id="element-1671">
            <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1672" />
            <input id="name" type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-white border border-[#D8D8D0] pl-11 pr-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="Jane Smith" data-id="element-1673" />
          </div>
        </div>

        <div data-id="element-1674">
          <FieldLabel htmlFor="email" data-id="element-1675">Email</FieldLabel>
          <div className="relative" data-id="element-1676">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1677" />
            <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#D8D8D0] pl-11 pr-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="you@example.com" data-id="element-1678" />
          </div>
        </div>

        <div data-id="element-1679">
          <FieldLabel htmlFor="password" data-id="element-1680">Password</FieldLabel>
          <div className="relative" data-id="element-1681">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1682" />
            <input id="password" type={showPassword ? 'text' : 'password'} required minLength={6} value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#D8D8D0] pl-11 pr-12 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="At least 6 characters" data-id="element-1683" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9A9A90] hover:text-tealslate transition-colors" tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-1684">
              {showPassword ? <EyeOff className="w-4 h-4" data-id="element-1685" /> : <Eye className="w-4 h-4" data-id="element-1686" />}
            </button>
          </div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer group" data-id="element-1687">
          <input type="checkbox" checked={marketingOptIn} onChange={e => setMarketingOptIn(e.target.checked)} className="mt-1 w-4 h-4 accent-tealslate cursor-pointer" data-id="element-1688" />
          <span className="text-sm text-[#3B3B3B] leading-relaxed" data-id="element-1689">
            Send me occasional updates on new books, supplements, and articles.
            No spam — unsubscribe anytime.
          </span>
        </label>

        {error && <ErrorBanner message={error} data-id="element-1690" />}

        <SubmitButton loading={loading} loadingLabel="Creating account…" data-id="element-1691">
          Create Account
        </SubmitButton>

        <p className="text-sm text-[#3B3B3B] text-center pt-4" data-id="element-1692">
          Already have an account?{' '}
          <Link to="/login" className="font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1693">
            Sign in
          </Link>
        </p>

        <p className="text-xs text-[#7A7A70] italic text-center pt-2" data-id="element-1694">
          By creating an account, you agree to receive a verification email.
        </p>
      </form>
    </AuthShell>;
}
// ─── Forgot Password ────────────────────────────────────────────
export function ForgotPassword() {
  const {
    resetPassword
  } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };
  return <AuthShell eyebrow="Reset Password" title="Forgot your password?" subtitle="Enter your email and we'll send you a link to reset it." data-id="element-1695">
      {sent ? <div className="bg-white border-l-4 border-tealslate p-8 flex gap-5" data-id="element-1696">
          <CheckCircle2 className="w-8 h-8 text-tealslate shrink-0" data-id="element-1697" />
          <div data-id="element-1698">
            <h3 className="text-2xl font-serif text-[#1F1F1F] mb-2" data-id="element-1699">
              Check your email.
            </h3>
            <p className="text-[#3B3B3B] leading-relaxed mb-4" data-id="element-1700">
              If an account exists for <strong data-id="element-1701">{email}</strong>, we've sent a
              password reset link. Check your spam folder if you don't see it.
            </p>
            <Link to="/login" className="text-xs font-bold text-tealslate uppercase tracking-wider hover:text-navyslate transition-colors inline-flex items-center gap-2" data-id="element-1702">
              Back to sign in <ArrowRight className="w-3 h-3" data-id="element-1703" />
            </Link>
          </div>
        </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1704">
          <div data-id="element-1705">
            <FieldLabel htmlFor="email" data-id="element-1706">Email</FieldLabel>
            <div className="relative" data-id="element-1707">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1708" />
              <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#D8D8D0] pl-11 pr-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="you@example.com" data-id="element-1709" />
            </div>
          </div>

          {error && <ErrorBanner message={error} data-id="element-1710" />}

          <SubmitButton loading={loading} loadingLabel="Sending…" data-id="element-1711">
            Send Reset Link
          </SubmitButton>

          <p className="text-sm text-[#3B3B3B] text-center pt-4" data-id="element-1712">
            Remembered it?{' '}
            <Link to="/login" className="font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1713">
              Back to sign in
            </Link>
          </p>
        </form>}
    </AuthShell>;
}